import { CloseOutlined, DownOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Button, Checkbox, Form, Input, message, Modal, Select, Space } from 'antd';
import type { FormInstance } from 'antd';
import jsCookie from 'js-cookie';
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LangText } from '@/components/LangText';
import { BUYER_INFO_KEY, TOKEN_KEY } from '@/constants';
import {
  DEFAULT_PHONE_DIAL_CODE,
  getPhoneDialConfig,
  getPhoneDialSelectOptions,
  isNationalNumberValid,
  nationalDigitsOnly,
} from '@/constants/phoneDialCodes';
import { useRootStore } from '@/contexts';
import {
  apiBuyerCaptchaSend,
  apiBuyerLoginCaptcha,
  apiBuyerLoginPassword,
  apiBuyerRegister,
  apiCaptchaCheck,
} from '@/service/api';
import { BuyerOperationTypeEnum, ChannelTypeEnum } from '@/service/api/types';
import { preFixImgUrl } from '@/utils';
import { subscribeAuthModal } from './authModalController';
import './index.scss';

type AccountType = 'phone' | 'email';
type AuthView = 'login' | 'register';
type LoginMode = 'password' | 'captcha';
type RegisterStep = 'account' | 'password';
type AccountPayload = {
  account: string;
  countryRegionCode?: string;
};

const MODAL_IMAGE = preFixImgUrl('4503b0c5bf07e090ba27fe709628bfa4.png');
const MODAL_LOGO_IMAGE = preFixImgUrl('abfe90c0281c853887d182df8eb6a929.png');
const LOGO_IMAGE = preFixImgUrl('41683676bd21e8531742d57a8f7a2570.png');
const AUTH_MODAL_STYLE = {
  '--ant-modal-content-padding': '0px',
} as CSSProperties;

const ACCOUNT_TABS = [
  { value: 'phone', labelKey: 'common.form.phone' },
  { value: 'email', labelKey: 'common.form.email' },
] as const;

export const AuthModalHost = () => {
  const { t } = useTranslation();
  const { userStore } = useRootStore();
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [registerPasswordForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [authView, setAuthView] = useState<AuthView>('login');
  const [accountType, setAccountType] = useState<AccountType>('phone');
  const [registerAccountType, setRegisterAccountType] = useState<AccountType>('phone');
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  const [registerStep, setRegisterStep] = useState<RegisterStep>('account');
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerCodeLoading, setRegisterCodeLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [registerCountdown, setRegisterCountdown] = useState(0);
  const [registerAccountInfo, setRegisterAccountInfo] = useState<AccountPayload & { captcha: string } | null>(null);

  const phoneDialOptions = useMemo(() => getPhoneDialSelectOptions(), []);
  const isCaptchaLogin = loginMode === 'captcha';

  useEffect(() => {
    return subscribeAuthModal((options) => {
      if (options?.reason === 'unauthorized') {
        userStore.clearUser();
      }
      setAuthView('login');
      setOpen(true);
    });
  }, [userStore]);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setCountdown((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (registerCountdown <= 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setRegisterCountdown((current) => current - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [registerCountdown]);

  const closeModal = () => {
    setOpen(false);
  };

  const resetRegister = () => {
    setRegisterStep('account');
    setRegisterAccountType('phone');
    setRegisterAccountInfo(null);
    registerForm.resetFields();
    registerPasswordForm.resetFields();
  };

  const switchToLogin = () => {
    setAuthView('login');
    resetRegister();
  };

  const switchToRegister = () => {
    setAuthView('register');
    setRegisterStep('account');
    setRegisterAccountInfo(null);
    registerPasswordForm.resetFields();
  };

  const openProtocolPage = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const handleAccountTypeChange = (value: AccountType) => {
    setAccountType(value);
    loginForm.resetFields();
  };

  const handleRegisterAccountTypeChange = (value: AccountType) => {
    setRegisterAccountType(value);
    registerForm.resetFields();
  };

  const validatePhone = (targetForm: FormInstance) => ({
    validator: (_: unknown, value: string) => {
      const dial = targetForm.getFieldValue('phoneDialCode') ?? DEFAULT_PHONE_DIAL_CODE;
      if (!value || !String(value).trim()) {
        return Promise.resolve();
      }
      if (!isNationalNumberValid(dial, String(value))) {
        const key = getPhoneDialConfig(dial).invalidMessageLngKey ?? 'auth.validation.phoneInvalid';
        return Promise.reject(new Error(t(key)));
      }
      return Promise.resolve();
    },
  });

  const getAccountPayload = async (targetForm: FormInstance, targetAccountType: AccountType): Promise<AccountPayload> => {
    if (targetAccountType === 'phone') {
      const values = await targetForm.validateFields(['phoneDialCode', 'phone']);
      const dial = values.phoneDialCode ?? DEFAULT_PHONE_DIAL_CODE;

      return {
        account: nationalDigitsOnly(values.phone),
        countryRegionCode: getPhoneDialConfig(dial).dialCode,
      };
    }

    const values = await targetForm.validateFields(['email']);

    return {
      account: values.email,
      countryRegionCode: undefined,
    };
  };

  const handleSendCaptcha = async () => {
    if (countdown > 0 || codeLoading) {
      return;
    }

    try {
      setCodeLoading(true);
      const payload = await getAccountPayload(loginForm, accountType);
      const res = await apiBuyerCaptchaSend({
        account: payload.account,
        countryRegionCode: payload.countryRegionCode,
        operationType: BuyerOperationTypeEnum.LOGIN,
      });

      if (res.code === 0) {
        message.success(t('auth.message.captchaSent'));
        setCountdown(60);
      }
    } catch (error: any) {
      if (error?.msg) {
        message.error(error.msg);
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const handleSendRegisterCaptcha = async () => {
    if (registerCountdown > 0 || registerCodeLoading) {
      return;
    }

    try {
      setRegisterCodeLoading(true);
      const payload = await getAccountPayload(registerForm, registerAccountType);
      const res = await apiBuyerCaptchaSend({
        account: payload.account,
        countryRegionCode: payload.countryRegionCode,
        operationType: BuyerOperationTypeEnum.REGISTER,
      });

      if (res.code === 0) {
        message.success(t('auth.message.captchaSent'));
        setRegisterCountdown(60);
      }
    } catch (error: any) {
      if (error?.msg) {
        message.error(error.msg);
      }
    } finally {
      setRegisterCodeLoading(false);
    }
  };

  const saveLoginInfo = (accessToken?: string, buyerInfo?: any) => {
    if (accessToken) {
      jsCookie.set(TOKEN_KEY, accessToken);
    }

    if (buyerInfo) {
      userStore.signIn(buyerInfo);
      localStorage.setItem(BUYER_INFO_KEY, JSON.stringify(buyerInfo));
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = await getAccountPayload(loginForm, accountType);

      if (isCaptchaLogin) {
        const values = await loginForm.validateFields(['captcha']);
        const res = await apiBuyerLoginCaptcha({
          account: payload.account,
          countryRegionCode: payload.countryRegionCode,
          captcha: values.captcha,
          channel: ChannelTypeEnum.WEB,
          operationType: BuyerOperationTypeEnum.LOGIN,
        });

        if (res.code === 0) {
          saveLoginInfo(res.data?.accessToken, res.data?.buyerInfo);
          message.success(t('auth.message.loginSuccess'));
          closeModal();
        }

        return;
      }

      const values = await loginForm.validateFields(['password']);
      const res = await apiBuyerLoginPassword({
        account: payload.account,
        countryRegionCode: payload.countryRegionCode,
        password: values.password,
      });

      if (res.code === 0) {
        saveLoginInfo(res.data?.accessToken, res.data?.buyerInfo);
        message.success(t('auth.message.loginSuccess'));
        closeModal();
      }
    } catch (error: any) {
      if (error?.msg) {
        message.error(error.msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterNext = async () => {
    try {
      setRegisterLoading(true);
      const payload = await getAccountPayload(registerForm, registerAccountType);
      const values = await registerForm.validateFields(['captcha', 'protocol']);
      const res = await apiCaptchaCheck({
        account: payload.account,
        captcha: values.captcha,
        countryRegionCode: payload.countryRegionCode,
        operationType: BuyerOperationTypeEnum.REGISTER,
      });

      if (res.data) {
        setRegisterAccountInfo({
          ...payload,
          captcha: values.captcha,
        });
        setRegisterStep('password');
        return;
      }

      message.error(t('auth.message.captchaInvalid'));
    } catch (error: any) {
      if (error?.msg) {
        message.error(error.msg);
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleRegisterSubmit = async () => {
    if (!registerAccountInfo) {
      return;
    }

    try {
      setRegisterLoading(true);
      const values = await registerPasswordForm.validateFields();
      const res = await apiBuyerRegister({
        account: registerAccountInfo.account,
        countryRegionCode: registerAccountInfo.countryRegionCode,
        password: values.password,
        confirmPassword: values.passwordRepeat,
        captcha: registerAccountInfo.captcha,
        channel: ChannelTypeEnum.WEB,
        agreeProtocol: true,
      });

      if (res.code === 0) {
        message.success(t('auth.message.registerSuccess'));
        switchToLogin();
      }
    } catch (error: any) {
      if (error?.msg) {
        message.error(error.msg);
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  const renderAccountTabs = (value: AccountType, onChange: (next: AccountType) => void) => (
    <div className="mt-[26px] mb-[24px] grid h-[54px] grid-cols-2 rounded-[8px] bg-[#F2F2F2] p-[2px]">
      {ACCOUNT_TABS.map((item) => (
        <button
          key={item.value}
          type="button"
          onClick={() => onChange(item.value as AccountType)}
          className={`rounded-[8px] text-[20px] transition-colors ${
            value === item.value ? 'bg-primary text-white' : 'text-[#979797]'
          }`}
        >
          <LangText lngKey={item.labelKey} />
        </button>
      ))}
    </div>
  );

  const renderPhoneField = (targetForm: FormInstance, disabled: boolean) => (
    <Form.Item
      required
      label={
        <LangText lngKey="common.form.phone" className="text-[20px] font-bold text-[#000]" />
      }
    >
      <Space.Compact className="w-full">
        <Form.Item name="phoneDialCode" noStyle initialValue={DEFAULT_PHONE_DIAL_CODE}>
          <Select
            options={phoneDialOptions}
            popupClassName="auth-login-phone-select-dropdown"
            classNames={{ popup: { root: 'auth-login-phone-select-dropdown' } }}
            className="auth-login-phone-select !w-[124px]"
            suffixIcon={<DownOutlined className="auth-login-phone-select-arrow" style={{ color: '#000' }} />}
            disabled={disabled}
            onChange={() => targetForm.validateFields(['phone']).catch(() => undefined)}
          />
        </Form.Item>
        <Form.Item
          name="phone"
          noStyle
          rules={[
            { required: true, message: t('auth.validation.phoneRequired') },
            validatePhone(targetForm),
          ]}
        >
          <Input placeholder={t('auth.placeholder.phone')} className="auth-login-input" disabled={disabled} />
        </Form.Item>
      </Space.Compact>
    </Form.Item>
  );

  const renderEmailField = (disabled: boolean) => (
    <Form.Item
      name="email"
      required
      validateTrigger="onBlur"
      label={
        <LangText lngKey="common.form.email" className="text-[20px] font-bold text-[#000]" />
      }
      rules={[
        { required: true, message: t('auth.validation.emailRequired') },
        { type: 'email', message: t('auth.validation.emailInvalid') },
      ]}
    >
      <Input placeholder={t('auth.placeholder.email')} className="auth-login-input" disabled={disabled} />
    </Form.Item>
  );

  const renderCaptchaField = (
    fieldName: string,
    disabled: boolean,
    sending: boolean,
    seconds: number,
    onSend: () => void,
    placeholderKey = 'auth.placeholder.captcha',
  ) => (
    <Form.Item
      required
      label={
        <LangText lngKey="common.form.captcha" className="text-[20px] font-bold text-[#000]" />
      }
    >
      <div className="auth-login-captcha-row">
        <Form.Item name={fieldName} noStyle rules={[{ required: true, message: t('auth.validation.captchaRequired') }]}>
          <Input placeholder={t(placeholderKey)} className="auth-login-input auth-login-captcha-input" disabled={disabled} />
        </Form.Item>
        <Button
          className="auth-login-captcha-button"
          disabled={seconds > 0 || sending}
          loading={sending}
          onClick={onSend}
        >
          {seconds > 0 ? `${seconds}s` : t('common.action.sendCaptcha')}
        </Button>
      </div>
    </Form.Item>
  );

  const renderLoginContent = () => (
    <div className="mx-auto w-[545px]">
      <img src={LOGO_IMAGE} alt="BilionPass" className="h-[42px] w-fit object-contain" draggable={false} />

      <LangText lngKey="auth.login.title" className="mt-[16px] block text-[32px] font-bold leading-[45px] text-[#000]" />
      <LangText lngKey="auth.login.subtitle" className="mt-[12px] block text-[24px] leading-[34px] text-[#222] font-normal" />

      {renderAccountTabs(accountType, handleAccountTypeChange)}

      <Form form={loginForm} layout="vertical" onFinish={handleSubmit}>
        {accountType === 'phone'
          ? renderPhoneField(loginForm, loading || codeLoading)
          : renderEmailField(loading || codeLoading)}

        {isCaptchaLogin ? (
          renderCaptchaField('captcha', loading, codeLoading, countdown, handleSendCaptcha, 'auth.placeholder.smsCaptcha')
        ) : (
          <Form.Item
            name="password"
            required
            label={
              <LangText lngKey="common.form.password" className="text-[20px] font-bold text-[#000]" />
            }
            rules={[{ required: true, message: t('auth.validation.passwordRequired') }]}
          >
            <Input.Password
              placeholder={t('auth.placeholder.password')}
              className="auth-login-password"
              disabled={loading}
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
        )}

        <div className="mt-[4px] flex items-center justify-between">
          <button
            type="button"
            onClick={() => setLoginMode(isCaptchaLogin ? 'password' : 'captcha')}
            className="text-[20px] leading-[28px] text-primary"
          >
            <LangText lngKey={isCaptchaLogin ? 'auth.login.usePassword' : 'auth.login.useCaptcha'} />
          </button>
          {/* <button
            type="button"
            onClick={() => message.info(t('auth.message.resetPasswordHint'))}
            className="text-[20px] leading-[28px] text-primary"
          >
            <LangText lngKey="auth.login.forgotPassword" />
          </button> */}
        </div>

        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          className="mt-[100px] !h-[66px] w-full !rounded-full !bg-primary !text-[24px]"
        >
          <LangText lngKey="auth.login.submit" />
        </Button>
      </Form>

      <div className="mt-[24px] flex items-center justify-center text-[20px] leading-[28px]">
        <LangText lngKey="auth.login.noAccount" className="text-[#000]" />
        <button type="button" onClick={switchToRegister} className="ml-[20px] text-[20px] leading-[28px] text-primary">
          <LangText lngKey="auth.register.submit" />
        </button>
      </div>
    </div>
  );

  const renderRegisterAccountContent = () => (
    <div className="auth-register-content mx-auto w-[545px]">
      <img src={LOGO_IMAGE} alt="BilionPass" className="h-[42px] w-fit object-contain" draggable={false} />

      <LangText lngKey="auth.register.submit" className="mt-[16px] block text-[32px] font-bold leading-[45px] text-[#000]" />
      <LangText lngKey="auth.register.subtitle" className="mt-[12px] block text-[24px] leading-[34px] text-[#222] font-normal" />

      {renderAccountTabs(registerAccountType, handleRegisterAccountTypeChange)}

      <Form form={registerForm} layout="vertical" onFinish={handleRegisterNext} initialValues={{ protocol: false }}>
        {registerAccountType === 'phone'
          ? renderPhoneField(registerForm, registerLoading || registerCodeLoading)
          : renderEmailField(registerLoading || registerCodeLoading)}

        {renderCaptchaField('captcha', registerLoading, registerCodeLoading, registerCountdown, handleSendRegisterCaptcha)}

        <Form.Item
          name="protocol"
          valuePropName="checked"
          className="!mb-0 !mt-[84px]"
          rules={[
            {
              validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error(t('auth.validation.protocolRequired')))),
            },
          ]}
        >
          <Checkbox className="auth-register-protocol">
            <LangText lngKey="auth.register.protocol.prefix" className="text-[20px] text-[#333]" />
            <LangText
              lngKey="auth.register.protocol.terms"
              className="mx-[2px] text-[20px] text-[#FF4D4F]"
              onClick={(event) => {
                event.stopPropagation();
                openProtocolPage('/terms');
              }}
            />
            <LangText lngKey="auth.register.protocol.and" className="text-[20px] text-[#333]" />
            <LangText
              lngKey="auth.register.protocol.privacy"
              className="mx-[2px] text-[20px] text-[#FF4D4F]"
              onClick={(event) => {
                event.stopPropagation();
                openProtocolPage('/privacy');
              }}
            />
          </Checkbox>
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          loading={registerLoading}
          className="mt-[18px] !h-[66px] w-full !rounded-full !bg-primary !text-[24px]"
        >
          <LangText lngKey="auth.register.next" />
        </Button>
      </Form>

      <div className="mt-[24px] flex items-center justify-center text-[20px] leading-[28px]">
        <LangText lngKey="auth.register.hasAccount" className="text-[#000]" />
        <button type="button" onClick={switchToLogin} className="ml-[20px] text-[20px] leading-[28px] text-primary">
          <LangText lngKey="auth.login.submit" />
        </button>
      </div>
    </div>
  );

  const renderRegisterPasswordContent = () => (
    <div className="auth-register-content auth-register-password-content mx-auto w-[545px]">
      <LangText lngKey="auth.register.setPassword" className="mt-[152px] mb-[72px] block text-[32px] font-bold leading-[45px] text-[#000]" />

      <Form form={registerPasswordForm} layout="vertical" onFinish={handleRegisterSubmit} className="mt-[70px]">
        <Form.Item
          name="password"
          required
          label={
            <LangText lngKey="common.form.password" className="text-[24px] font-bold text-[#000]" />
          }
          rules={[
            { required: true, message: t('auth.validation.passwordRequired') },
            { min: 6, message: t('auth.validation.passwordLength') },
            { max: 12, message: t('auth.validation.passwordLength') },
            { pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/, message: t('auth.validation.passwordLetterNumber') },
          ]}
        >
          <Input.Password
            placeholder={t('auth.placeholder.passwordRule')}
            className="auth-login-password"
            disabled={registerLoading}
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>
        <Form.Item
          name="passwordRepeat"
          required
          dependencies={['password']}
          label={
            <LangText lngKey="auth.register.confirmPassword" className="text-[24px] font-bold text-[#000]" />
          }
          rules={[
            { required: true, message: t('auth.validation.confirmPasswordRequired') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('auth.validation.passwordMismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={t('auth.placeholder.confirmPassword')}
            className="auth-login-password"
            disabled={registerLoading}
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          loading={registerLoading}
          className="mt-[100px] !h-[66px] w-full !rounded-full !bg-primary !text-[24px]"
        >
          <LangText lngKey="auth.register.submit" />
        </Button>
      </Form>
    </div>
  );

  return (
    <Modal
      open={open}
      centered
      width={1345}
      footer={null}
      closable={false}
      destroyOnHidden
      rootClassName="auth-login-modal-root"
      className="auth-login-modal"
      style={AUTH_MODAL_STYLE}
      onCancel={closeModal}
    >
      <div className="flex h-[862px] overflow-hidden rounded-[20px] bg-white">
        <div
          className="flex h-full w-[700px] shrink-0 items-end bg-cover bg-center"
          style={{ backgroundImage: `url(${MODAL_IMAGE})` }}
        >
          <div className="pb-[74px] pl-[45px] text-white">
            <LangText lngKey="auth.welcome" className="block text-[50px] font-bold leading-[70px]" />
            <img
              src={MODAL_LOGO_IMAGE}
              alt="BilionPass"
              className="mt-[8px] h-[82px] w-auto object-contain"
              draggable={false}
            />
          </div>
        </div>

        <div className="relative flex flex-1 flex-col px-[48px] pt-[48px]">
          <button
            type="button"
            onClick={closeModal}
            className="absolute right-[22px] top-[22px] flex h-[32px] w-[32px] items-center justify-center text-[20px] text-[#8C8C8C] transition-colors hover:text-[#333]"
          >
            <CloseOutlined />
          </button>

          {authView === 'login' && renderLoginContent()}
          {authView === 'register' && registerStep === 'account' && renderRegisterAccountContent()}
          {authView === 'register' && registerStep === 'password' && renderRegisterPasswordContent()}
        </div>
      </div>
    </Modal>
  );
};
